import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CountUp from 'react-countup';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { getApproRapport } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeStatusBadge, NdoeImage, NdoeError, NdoeLoading,
  useFicheArticle,
} from '../../components/ndoe';
// Pont jetons CSS → canvas : le graphique lit la charte au lieu de figer des
// couleurs. Le jour où la charte bascule, il suit sans être retouché.
import { jetons, alpha, optionsBase, fusionner } from '../../components/ndoe/chartTheme';
import { toCsv, downloadCsv } from './exportCsv';
import { money, dateTxt } from './format';
import './rapports.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

export default function RapportApprovisionnement() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const navigate = useNavigate();
  const { openFiche, ficheElement } = useFicheArticle();

  const [period, setPeriod] = useState({ period: 30 });
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
  }), [period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getApproRapport(apiParams);
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || "Impossible de charger le rapport d'approvisionnement.");
    } finally { setLoading(false); }
  }, [apiParams]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const handleExport = () => {
    if (!payload) return;
    setExporting(true);
    try {
      const blocs = [
        ...payload.serie_mensuelle.map((m) => ({ type: 'Mois', nom: m.mois, recu: m.recu, vendu: m.vendu })),
        ...payload.top_recus.map((p) => ({ type: 'Produit reçu', nom: `${p.nom_produit}${p.code_produit ? ` (${p.code_produit})` : ''}`, recu: p.quantite, vendu: '' })),
        ...payload.par_marque.map((m) => ({ type: 'Marque', nom: m.marque, recu: m.quantite, vendu: '' })),
      ];
      const columns = [
        { title: 'Type', value: (r) => r.type },
        { title: 'Libellé', value: (r) => r.nom },
        { title: 'Reçu (paires)', value: (r) => r.recu },
        { title: 'Vendu (paires)', value: (r) => r.vendu },
      ];
      downloadCsv(`approvisionnement_${payload.meta.start_date}_${payload.meta.end_date}.csv`, toCsv(columns, blocs));
      toast.success('Rapport exporté.');
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  const kpis = payload?.kpis || {};
  const va = kpis.valeur_achats || {};
  const meta = payload?.meta || {};
  const balanceTone = kpis.balance > 0 ? 'warn' : kpis.balance < 0 ? 'info' : 'ok';

  // Couleurs lues dans la CHARTE (pont chartTheme) — appel DANS le rendu, jamais
  // au niveau module : au chargement du module le CSS n'est pas garanti appliqué,
  // getComputedStyle rendrait du vide et le pont retomberait EN SILENCE sur ses
  // valeurs de repli.
  const t = jetons();
  // Les deux séries répondent à la question du titre : « est-ce que j'achète au
  // rythme où je vends ? ». L'or porte l'ENTRÉE (achat/réception, la couleur de
  // l'argent dépensé dans toute l'app), le bleu nuit la SORTIE (vendu). Deux
  // familles opposées : les barres restent lisibles côte à côte, y compris pour
  // un daltonien — un doré clair contre un doré foncé ne l'aurait pas été.
  // La translucidité laisse voir la grille derrière les barres hautes.
  const chartData = payload ? {
    labels: payload.serie_mensuelle.map((m) => m.mois.slice(5) + '/' + m.mois.slice(2, 4)),
    datasets: [
      { label: 'Reçu', data: payload.serie_mensuelle.map((m) => m.recu), backgroundColor: alpha(t.gold, 0.75), borderRadius: 5, maxBarThickness: 26 },
      { label: 'Vendu', data: payload.serie_mensuelle.map((m) => m.vendu), backgroundColor: alpha(t.ink, 0.55), borderRadius: 5, maxBarThickness: 26 },
    ],
  } : null;
  // optionsBase apporte la grille, les axes, la légende et l'info-bulle aux
  // couleurs de la charte, plus une animation sobre (420 ms au lieu de 900,
  // coupée si l'utilisateur a demandé moins de mouvement au niveau système).
  // Le libellé « paire(s) » est conservé : il porte l'unité métier du rapport.
  const chartOptions = fusionner(optionsBase(), {
    plugins: {
      tooltip: { callbacks: { label: (i) => ` ${i.dataset.label} : ${i.parsed.y} paire(s)` } },
    },
  });

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Approvisionnement"
        subtitle="Est-ce que j'achète au rythme où je vends ? Entrées, sorties et valeur d'achat."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        {meta.start_date && <span className="pp-range">du {dateTxt(meta.start_date)} au {dateTxt(meta.end_date)}</span>}
      </div>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : loading || !payload ? (
        <NdoeLoading rows={6} />
      ) : (
        <>
          <div className="rv-kpis">
            <div className="rv-kpi"><small>Paires reçues</small><b className="num"><CountUp end={kpis.paires_recues} duration={0.9} /></b></div>
            <div className="rv-kpi"><small>Paires vendues</small><b className="num"><CountUp end={kpis.paires_vendues} duration={0.9} /></b></div>
            <div className="rv-kpi">
              <small>Balance</small>
              <b className="num">{kpis.balance > 0 ? '+' : ''}{kpis.balance}</b>
              <NdoeStatusBadge kind="custom" tone={balanceTone}
                label={kpis.balance > 0 ? 'le stock gonfle' : kpis.balance < 0 ? 'le stock se vide' : 'équilibré'} />
            </div>
            <div className="rv-kpi rv-kpi--gold">
              <small>{va.couverture_pct !== null ? `Valeur d'achat (${va.couverture_pct} % costées)` : "Valeur d'achat"}</small>
              <b className="num">{va.montant !== null && va.montant !== undefined ? money(va.montant) : '—'}</b>
              <small>{va.paires} paire(s) · {va.lots || 0} lot(s)</small>
            </div>
          </div>

          <p className="pp-note">
            <NdoeStatusBadge kind="custom" tone={balanceTone} label="Lecture" />
            {' '}{payload.highlights.lecture_balance}
            {' '}Les décisions de réassort détaillées sont dans{' '}
            <button type="button" className="ndoe-fiche__lien" onClick={() => navigate('/rapports/ruptures')}>Ruptures →</button>
            {' '}et{' '}
            <button type="button" className="ndoe-fiche__lien" onClick={() => navigate('/rapports/decisions')}>Décisions →</button>
          </p>

          <section className="ndoe-card rv-chart">
            <div className="ndoe-section__title">Reçu vs vendu — 12 derniers mois</div>
            <div className="rv-chart__zone"><Bar data={chartData} options={chartOptions} /></div>
          </section>

          {payload.top_recus.length > 0 && (
            <>
              <h3 className="ve-titre">Reçus sur la période</h3>
              <div className="rv-produits">
                {payload.top_recus.map((p, i) => (
                  <button type="button" className="rv-produit" key={p.id_produit} onClick={() => openFiche(p.id_produit)}>
                    <span className="rv-produit__rang num">{i + 1}</span>
                    <NdoeImage src={p.img} alt={p.nom_produit} size="md" preview={false} />
                    <span className="rv-produit__txt">
                      <b>{p.nom_produit}</b>
                      <small>{p.code_produit || '—'}{p.marque ? ` · ${p.marque}` : ''}</small>
                    </span>
                    <span className="rv-produit__chiffres">
                      <b className="num">{p.quantite} paire(s)</b>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {va.couverture_pct !== null && va.couverture_pct < 100 && (
            <p className="pp-note">
              <NdoeStatusBadge kind="custom" tone="warn" label="Coûts" />
              {' '}Seules {va.couverture_pct} % des lignes reçues ont un coût d'achat : la valeur affichée est
              partielle. Complétez dans «&nbsp;Réceptions → Coûts d'achat&nbsp;» (saisie ou rattrapage en masse).
            </p>
          )}
        </>
      )}
      {ficheElement}
    </NdoePage>
  );
}
